/**
 * What one workspace has configured for itself.
 *
 * Unrelated things behind one door: the agent that runs its large jobs, the MCP
 * server its members point their AI clients at, and the row history it keeps.
 * They share nothing but the workspace and the fact that each is an
 * administrative decision -- which is why the menu entry is admin-gated and
 * nothing inside re-checks it.
 *
 * The sections are `Tabs` with a vertical orientation rather than a hand-rolled
 * list: the pattern wants roving focus and one tab stop, and Radix already
 * emits `role="tablist"`, the arrow-key handling and the panel association.
 */
import { Bot, History, Plug } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { UserWorkspace } from '@/api/types'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { AgentSection } from '../settings/components/agent-section'
import { HistorySection } from '../settings/components/history-section'
import { McpSection } from '../settings/components/mcp-section'

export function WorkspaceSettingsDialog({
  workspace,
  open,
  onOpenChange,
}: {
  workspace: UserWorkspace
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] sm:max-w-5xl flex-col">
        <DialogHeader>
          <DialogTitle>{t('workspaceSettings.title')}</DialogTitle>
          <DialogDescription>
            {t('workspaceSettings.description', { name: workspace.name })}
          </DialogDescription>
        </DialogHeader>

        <Tabs
          defaultValue="agent"
          orientation="vertical"
          className="min-h-0 flex-1 flex-row items-stretch gap-6"
        >
          <TabsList
            variant="line"
            aria-label={t('workspaceSettings.sections')}
            className="w-48 shrink-0 items-start justify-start"
          >
            <TabsTrigger value="agent">
              <Bot />
              {t('workspaceSettings.sectionAgent')}
            </TabsTrigger>
            <TabsTrigger value="mcp">
              <Plug />
              {t('workspaceSettings.sectionMcp')}
            </TabsTrigger>
            <TabsTrigger value="history">
              <History />
              {t('workspaceSettings.sectionHistory')}
            </TabsTrigger>
          </TabsList>

          {/*
            Each panel fetches its own answer, and only once it is on screen:
            Radix unmounts an inactive tab, so opening the dialog costs one
            request rather than one per tab.
          */}
          <TabsContent value="agent" className="min-h-0 flex-1 overflow-y-auto pr-1">
            <AgentSection workspaceId={workspace.id} />
          </TabsContent>
          <TabsContent value="mcp" className="min-h-0 flex-1 overflow-y-auto pr-1">
            <McpSection />
          </TabsContent>
          <TabsContent value="history" className="min-h-0 flex-1 overflow-y-auto pr-1">
            <HistorySection workspaceId={workspace.id} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
